import { ElementType } from 'react'

import {
  ReviewFollowButton,
  AuthorRelationship,
  Author,
  ReviewVisitInfo,
  VisitKeywordsProps,
  ViewCount,
  LegalStatus,
} from '@place/ui'
import { ReviewCardComponentType, ReviewCardProps } from '@place/ui/presets'
import dayjs from 'dayjs'
import dynamic from 'next/dynamic'

import { isNotEmptyArray } from '@local/utils'

import ErrorBoundary from '@web/components/ErrorBoundary'
import { ReactionArea } from '@web/components/ReactionArea/ReactionArea'
import {
  PlaceOtherReview_ReviewGroupFragment,
  PlaceType,
  ReviewContent_ReviewGroupFragment,
  ReviewGroupExposeStatus,
} from '@web/gql/graphql'
import { useFollow } from '@web/hooks/useFollow'
import { useGoTo } from '@web/hooks/useGoTo'
import useMe from '@web/hooks/useMe'
import routes from '@web/libs/constants/routes'
import nClicks from '@web/libs/nlog/click/codes'
import { convertReply, convertVisitKeywords, isRestaurantReview } from '@web/libs/reviewGroup.helper'
import { truncateNumber } from '@web/libs/utils'
import { displayProofName } from '@web/libs/visit.helper'
import MoreReviewContent from '@web/pages/review/components/ReviewDetail/ReviewContent/components/MoreReviewContent'
import ContextMenuReview from '@web/pages/review/components/ReviewDetail/ReviewContent/ReviewProfile/ContextMenuReview/ContextMenuReview'
import ReviewStatusNotification from '@web/pages/review/components/ReviewDetail/ReviewContent/ReviewStatusNotification'

import styles from './index.module.scss'

dayjs.locale('ko')

const DynamicMediaList = dynamic(() => import('@web/components/MediaList'))

export const resolveLegalStatus = (exposeStatus: ReviewGroupExposeStatus): LegalStatus => {
  if (exposeStatus === ReviewGroupExposeStatus.UnexposeTemporarily) {
    return 'TEMPORARILY_SUSPENDED'
  }

  if (exposeStatus === ReviewGroupExposeStatus.UnexposePermanently) {
    return 'PERMANENTLY_SUSPENDED'
  }

  return 'ACTIVE'
}

type ReviewType = ReviewContent_ReviewGroupFragment | PlaceOtherReview_ReviewGroupFragment

const components: ReviewCardComponentType[] = [
  'afterTextArea',
  'profile',
  'mediaArea',
  'rating',
  'visitKeywords',
  'text',
  'votedKeywords',
  'beforeEtcArea',
  'etcArea',
  'reply',
]

export enum ReviewEndArea {
  MAIN = 'reviewEnd', // 리뷰 본문
  OTHERS = 'reviewEndOthers', // 이 장소의 다른 리뷰
}

export const useReviewCardPropsForReviewEnd = (
  reviewGroup: ReviewType,
  area: ReviewEndArea,
): ReviewCardProps<ElementType> => {
  const { me } = useMe()
  const isEntryReview = area === ReviewEndArea.MAIN

  const clickCodeArea = nClicks[area]

  const { handleFollowClick } = useFollow(reviewGroup.author!, {
    followRequest: clickCodeArea.FOLLOW,
    cancelFollowReqeust: clickCodeArea.FOLLOWREQUESTCANCEL,
    cancelFollow: clickCodeArea.UNFOLLOW,
  })

  const relationship: AuthorRelationship =
    me?.idno === reviewGroup.author?.idno
      ? 'ME'
      : reviewGroup.author?.didIRequestFollow
      ? 'FOLLOW_REQUESTED'
      : reviewGroup.author?.isFriend
      ? 'FOLLOWING'
      : 'NO_RELATIONSHIP'

  const author: Author = {
    nickname: reviewGroup.author?.profile.nickname ?? '',
    borderImageUrl: reviewGroup.author?.profile.border?.imageUrl ?? undefined,
    profileImageUrl: reviewGroup.author?.profile.originImageUrl ?? undefined,
    statTexts: [
      `리뷰 ${reviewGroup.author?.stat.reviewCount}`,
      `사진 ${reviewGroup.author?.stat.imageCount}`,
      `팔로워 ${reviewGroup.author?.stat.followerCount}`,
    ],
  }

  const showMoreReviewContent =
    relationship === 'ME' &&
    reviewGroup.mediaItems.length === 0 &&
    !reviewGroup.text &&
    reviewGroup.exposeStatus === ReviewGroupExposeStatus.Expose

  const followClickCode =
    relationship === 'FOLLOWING'
      ? clickCodeArea.UNFOLLOW
      : relationship === 'FOLLOW_REQUESTED'
      ? clickCodeArea.FOLLOWREQUESTCANCEL
      : relationship === 'NO_RELATIONSHIP'
      ? clickCodeArea.FOLLOW
      : undefined

  const profileExtra = (
    <>
      {relationship !== 'ME' && (
        <ReviewFollowButton relationship={relationship} onClick={handleFollowClick} clickCode={followClickCode} />
      )}
      <ErrorBoundary fallback={null}>
        <div className={styles.btn_context_wrapper}>
          <ContextMenuReview
            reviewGroup={reviewGroup}
            nClickArea={clickCodeArea}
            redirectToReviewHome={isEntryReview}
          />
        </div>
      </ErrorBoundary>
    </>
  )

  const keywords = reviewGroup.votedKeywords.map(({ code, icon, text }) => ({
    code,
    iconUrl: icon,
    name: text.korean ?? '',
  }))

  const mediaArea = isNotEmptyArray(reviewGroup.mediaItems) ? (
    <div className={styles.photo_area_wrapper}>
      <DynamicMediaList reviewGroup={reviewGroup} nClickArea={area} />
    </div>
  ) : showMoreReviewContent ? (
    <MoreReviewContent reviewGroup={reviewGroup} />
  ) : null

  const visitedDateTime = dayjs(reviewGroup.visit?.visitedDateTime)
  const isPastYear = visitedDateTime.isBefore(dayjs(), 'year')
  const proofName = displayProofName(reviewGroup.selectedProof.type)
  const visitDateText = visitedDateTime.format(`${isPastYear ? 'YY.' : ''}M.D.dd`)

  const beforeEtcArea = (
    <ReviewVisitInfo
      visitDateText={visitDateText}
      visitDateTextForA11y={visitedDateTime.format(`${isPastYear ? 'YY' : ''}년 M월 D일 dd요일`)}
      proofName={proofName ?? undefined}
      visitCountText={
        reviewGroup.visit?.visitCount ? `${truncateNumber(reviewGroup.visit.visitCount)}번째 방문` : undefined
      }
    />
  )

  const viewCount = reviewGroup.viewCount ?? 0

  const etcArea = (
    <>
      <div className={styles.etc_left_area}>
        <ReactionArea reviewGroup={reviewGroup} nClickArea={nClicks.reviewEnd} />
      </div>
      {viewCount > 0 && (
        <div className={styles.etc_right_area}>
          <ViewCount count={truncateNumber(viewCount)} />
        </div>
      )}
    </>
  )

  const replyProps = convertReply(reviewGroup)
  const visitCategories = convertVisitKeywords(reviewGroup)
  const showVisitKeywords = isRestaurantReview(reviewGroup) && reviewGroup.visitedDateTime
  const visitKeywordsProps: VisitKeywordsProps | undefined = showVisitKeywords || visitCategories
    ? {
      ...(showVisitKeywords && { visitDateTime: reviewGroup.visitedDateTime }),
      visitCategories: visitCategories || [],
      clickCode: nClicks.reviewEnd.VISIT_KEYWORD_MORE,
    }
    : undefined

  const { goTo } = useGoTo()

  const handleClickProfile = () => {
    const isMyReview = me?.id === reviewGroup.author?.id
    const isValidReview = !!reviewGroup.author?.id

    if (!isValidReview) return

    if (isMyReview) {
      goTo(routes.HOME, { openWindow: true })
    } else {
      if (reviewGroup.author?.id)
        goTo(routes.HOME_REVIEW, { params: { userId: reviewGroup.author?.id }, openWindow: true })
    }
  }

  const afterTextArea = <ReviewStatusNotification exposeStatus={reviewGroup.exposeStatus} />

  const status = resolveLegalStatus(reviewGroup.exposeStatus)
  const isHotelPlace = reviewGroup.place?.type === PlaceType.Global

  return {
    components,
    initialFolded: area === ReviewEndArea.OTHERS,
    afterTextArea,
    profileProps: {
      author,
      relationship,
      extra: profileExtra,
      showRelationLabel: false,
      clickCode: clickCodeArea.PROFILE,
      onClick: handleClickProfile,
    },
    mediaArea,
    visitKeywordsProps,
    textProps: {
      text: reviewGroup.text ?? '',
      foldableEllipsisProps: {
        clickCode: clickCodeArea.TEXT,
      },
    },
    votedKeywordsProps: { keywords },
    beforeEtcArea,
    etcArea,
    replyProps,
    status,
    rating: isHotelPlace ? reviewGroup.rating ?? undefined : undefined,
  }
}
